import {Component, Input} from '@angular/core';
import {CommonModule} from '@angular/common';

/** The five-state matrix from the Epic's DCB0129 hazard log (HAZ-W2/W3/W4):
 *  every "nothing rendered" case in the weekly panels must resolve to one of
 *  these explicit, distinguishable states — never a shared blank/empty path. */
export type WeeklyStateLabelState =
    | 'noCheckIn'
    | 'skip'
    | 'notRated'
    | 'nothingToReport'
    | 'notApplicable';

/** Default copy per state. `skip` has no sensible generic default — the caller
 *  always supplies the chip's verbatim text via `text`. */
const DEFAULT_TEXT: Record<WeeklyStateLabelState, string> = {
    noCheckIn: 'No check-in',
    skip: '',
    notRated: 'Not rated',
    nothingToReport: 'Nothing to report',
    notApplicable: 'N/A',
};

/**
 * Shared five-state label (UNIVAPLAT-3034). Every v2 weekly panel renders one
 * of five distinct "no value" states rather than a single shared empty path:
 *
 *  - `noCheckIn`       — no check-in submitted at all (grey, "No check-in").
 *  - `skip`            — the respondent explicitly skipped this item; `text`
 *                        carries the chip's verbatim copy from the payload.
 *  - `notRated`        — presented but left unanswered ("Not rated").
 *  - `nothingToReport` — a positive/affirmative answer of "nothing happened"
 *                        (green) — clinically distinct from missing data.
 *                        Panels may override `text` with their own affirmative
 *                        copy (e.g. "No stress ratings this week").
 *  - `notApplicable`   — the item does not apply this week ("N/A").
 */
@Component({
    selector: 'cdss-weekly-state-label',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './weekly-state-label.component.html',
    styleUrl: './weekly-state-label.component.scss'
})
export class WeeklyStateLabelComponent {
    @Input() state: WeeklyStateLabelState = 'noCheckIn';

    /** Overrides the default copy — required for `skip`, optional elsewhere
     *  (e.g. a panel-specific affirmative message for `nothingToReport`). */
    @Input() text?: string;

    get label(): string {
        return this.text ?? DEFAULT_TEXT[this.state];
    }

    get styleClass(): string {
        return `weekly-state-label--${this.kebabState}`;
    }

    private get kebabState(): string {
        return this.state.replace(/([A-Z])/g, '-$1').toLowerCase();
    }
}
